import { isPlatformBrowser } from '@angular/common';
import {
  DOCUMENT,
  EnvironmentProviders,
  inject,
  PLATFORM_ID,
  Provider,
} from '@angular/core';
import { environment } from '@environments/environment';
import { provideOAuthClient, UrlHelperService } from 'angular-oauth2-oidc';
import {
  Observable,
  interval,
  startWith,
  map,
  distinctUntilChanged,
  EMPTY,
  takeWhile,
  last,
  switchMap,
} from 'rxjs';
import { createMongoAbility, PureAbility } from '@casl/ability';

export const authProviders = (): (Provider | EnvironmentProviders)[] => {
  return [
    provideOAuthClient({
      resourceServer: {
        allowedUrls: [environment.baseUrl, environment.lanceServicoUrl],
        sendAccessToken: true,
      },
    }),
    {
      provide: UrlHelperService,
      useFactory: () => new CustomUrlHelperService(),
    },
    {
      provide: AuthHelper,
      useFactory: () => new AuthHelper(),
    },
    { provide: PureAbility, useValue: createMongoAbility() },
  ];
};

export class CustomUrlHelperService implements UrlHelperService {
  private document = inject(DOCUMENT);
  getHashFragmentParams(customHashFragment: string) {
    let hash2 = customHashFragment || this.document.location.hash;
    hash2 = decodeURIComponent(hash2);
    if (hash2.indexOf('#') !== 0) {
      return {};
    }
    const questionMarkPosition = hash2.indexOf('?');
    if (questionMarkPosition > -1) {
      hash2 = hash2.slice(questionMarkPosition + 1);
    } else {
      hash2 = hash2.slice(1);
    }
    return this.parseQueryString(hash2);
  }
  parseQueryString(queryString: string) {
    const data: Record<string, string> = {};
    let separatorIndex, escapedKey, escapedValue, key, value;
    if (queryString === null) {
      return data;
    }
    const pairs = queryString.split('&');
    for (const pair of pairs) {
      separatorIndex = pair.indexOf('=');
      if (separatorIndex === -1) {
        escapedKey = pair;
        escapedValue = null;
      } else {
        escapedKey = pair.slice(0, separatorIndex);
        escapedValue = pair.slice(separatorIndex + 1);
      }
      key = decodeURIComponent(escapedKey);
      value = decodeURIComponent(escapedValue!);
      if (key.slice(0, 1) === '/') {
        key = key.slice(1);
      }
      data[key] = value;
    }
    return data;
  }
}

export class AuthHelper {
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  watchQueryParams$(retryIntervalMs = 100): Observable<Record<string, string>> {
    if (isPlatformBrowser(this.platformId)) {
      return interval(retryIntervalMs).pipe(
        startWith(0),
        map(() => new URLSearchParams(this.document.location.search)),
        map((params) => {
          const result: Record<string, string> = {};
          params.forEach((value, key) => {
            result[key] = value;
          });
          return result;
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      );
    } else {
      return EMPTY;
    }
  }
  checkCodeExisting(
    paramName: string,
    conditionFn: () => boolean,
    maxRetries = 10,
    retryIntervalMs = 100
  ) {
    return this.watchQueryParams$(retryIntervalMs).pipe(
      takeWhile((params, index) => !!params[paramName] && index < maxRetries),
      last(null, {} as Record<string, string>),
      switchMap((params) =>
        interval(retryIntervalMs).pipe(
          startWith(0),
          takeWhile(() => !!params[paramName] && conditionFn()),
          last(null, params)
        )
      ),
      map(() => ({} as Record<string, string>))
    );
  }
}
